import { Eye } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

interface SampleContact {
  id: number;
  first_name: string | null;
  last_name: string | null;
}

const contactLabel = (contact: SampleContact) =>
  `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim() ||
  `Contact ${contact.id}`;

/**
 * Renders a template the way the customer will actually receive it.
 *
 * Tokens are resolved server-side at send (see the token-rendering migration),
 * which is correct but means the author of "Hi {{first_name}}," never sees the
 * result until it has already gone out. This runs the SAME renderer through
 * `preview_message_body`, nothing is stored and nothing is sent, so a
 * mistyped token shows up as a hole in the sentence here rather than in a
 * customer's inbox.
 *
 * The preview is against a real contact rather than invented sample data: a
 * placeholder like "John Smith" hides the case this is most useful for, a
 * contact with no first name.
 */
export const MessagePreview = ({
  body,
  subject,
}: {
  body: string;
  subject?: string | null;
}) => {
  const workspaceId = getActiveWorkspaceId();
  const [open, setOpen] = useState(false);
  const [contactId, setContactId] = useState<number | null>(null);

  const { data: contacts } = useQuery<SampleContact[]>({
    queryKey: ["preview-contacts", workspaceId],
    enabled: Boolean(workspaceId) && open,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("contacts")
        .select("id,first_name,last_name")
        .eq("workspace_id", workspaceId!)
        .order("last_seen", { ascending: false })
        .limit(25);
      if (error) throw new Error(error.message);
      return (data ?? []) as SampleContact[];
    },
  });

  const chosen = contactId ?? contacts?.[0]?.id ?? null;

  const { data: preview, isFetching } = useQuery<{
    body: string;
    subject: string | null;
  } | null>({
    queryKey: ["message-preview", chosen, body, subject],
    enabled: open && chosen != null && body.trim().length > 0,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc(
        "preview_message_body",
        { p_body: body, p_contact_id: chosen, p_subject: subject ?? null },
      );
      if (error) throw new Error(error.message);
      const row = (data as { body: string; subject: string | null }[])?.[0];
      return row ?? null;
    },
  });

  if (!body.trim()) return null;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-fit items-center gap-1 text-[11.5px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
      >
        <Eye className="size-3.5" />
        Preview with real details
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-border bg-muted/40 p-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
          Preview as
          <select
            className="h-7 max-w-[11rem] rounded-md border border-input bg-transparent px-1.5 text-[12px] text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={chosen ?? ""}
            onChange={(event) => setContactId(Number(event.target.value))}
          >
            {(contacts ?? []).map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contactLabel(contact)}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[11.5px] text-muted-foreground hover:text-foreground"
        >
          Hide
        </button>
      </div>

      {isFetching ? (
        <p className="text-[12px] text-muted-foreground">Rendering…</p>
      ) : preview ? (
        <div className="min-w-0">
          {preview.subject ? (
            <p className="truncate text-[12.5px] font-medium text-foreground">
              {preview.subject}
            </p>
          ) : null}
          <p className="whitespace-pre-wrap break-words text-[12.5px] text-foreground">
            {preview.body}
          </p>
        </div>
      ) : (
        <p className="text-[12px] text-muted-foreground">
          Add a contact to this workspace to preview.
        </p>
      )}
    </div>
  );
};
